<script setup lang="ts">
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { cn } from "@/utils/libs/tailwind-utils";

import Skeleton from "primevue/skeleton";

import BaseDialog from "@/components/ui/dialog/base-dialog.vue";

interface IProps {
  title: string;
  infos?: string;
  isLoading?: boolean;
  content?: string;
  contentClass?: string;
}

const isInfosDialogVisible = defineModel<boolean>("infosVisible", {
  required: false,
  default: false,
});

const props = defineProps<IProps>();
</script>

<template>
  <div
    v-if="props.isLoading"
    v-bind="$attrs"
  >
    <Skeleton class="!h-28 w-full flex" />
  </div>
  <template v-else>
    <div
      class="insights-card h-min-28"
      v-bind="$attrs"
    >
      <p class="insights-card__title">
        <i
          aria-controls="insights-card-infos-dialog"
          :aria-expanded="isInfosDialogVisible"
          :aria-label="props.title"
          class="pi pi-info-circle cursor-pointer"
          :data-test-id="DATA_TEST_IDS.views.dashboard.card.cardInfosButton"
          role="button"
          tabindex="0"
          @click="isInfosDialogVisible = true"
          @keydown.enter="isInfosDialogVisible = true"
        />
        <span>
          {{ props.title }}
        </span>
        <slot name="titleRight" />
      </p>

      <slot>
        <span :class="cn('text-2xl font-semibold whitespace-nowrap', props.contentClass)">
          {{ props.content }}
        </span>
      </slot>

      <slot name="bottom" />
    </div>
  </template>

  <BaseDialog
    id="insights-card-infos-dialog"
    v-model:visible="isInfosDialogVisible"
    :data-test-id="DATA_TEST_IDS.views.dashboard.card.cardInfosDialog"
    modal
    size="medium"
  >
    <template #header>
      <h4 class="font-semibold">
        {{ props.title }}
      </h4>
    </template>

    <slot name="infos">
      <p class="whitespace-pre-line">
        {{ props.infos }}
      </p>
    </slot>
  </BaseDialog>
</template>

<style lang="scss" scoped>
.insights-card {
  @apply border rounded-md p-4;
  @apply flex flex-col gap-2;

  &__title {
    @apply flex items-center gap-1 text-text-secondary;

    span {
      @apply uppercase text-xs;
    }

    i {
      @apply text-xs !important;
    }
  }
}
</style>
